"use client";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { isEqual } from "es-toolkit";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocalStorage } from "usehooks-ts";

import type {
  BasicSort,
  FilterRecord,
  FiltersSortContextType,
  SortVariant,
} from "@/contexts/filters-sort-context";
import {
  FilterType,
  SortType,
  useFiltersAndSort,
} from "@/contexts/filters-sort-context";
import { UserContext } from "@/contexts/user-context";
import type {
  CreatorStat,
  CreatorStatsOrderBy,
  TokenMetadatum,
} from "@/graphql/codegen/graphql";
import {
  CreatorStatsDocument,
  TokenMetadataQueryDocument,
} from "@/graphql/codegen/graphql";
import { usePaginationRequest } from "@/hooks-api/use-pagination-request";
import { PINNED_TOKEN_PUBLIC_KEYS } from "@/utils/constants";
import { FocusDateParser } from "@/utils/date";
import { LocalStorageKeys } from "@/utils/local-storage-keys";

import {
  DiscoverFilterFields,
  SORT_KEYS,
  SORT_VALUE_MAP,
  TIME_RANGE_SORT_KEYS,
  TimePeriod,
} from "../discover-sort-constants";

export type DiscoverContextType = {
  lazyLoadingMarkup: ReactNode;
  lazyLoadingRef: (node: HTMLElement | null) => void;
  creators: Array<CreatorStat | TokenMetadatum>;
  pinnedCreators: Array<CreatorStat | TokenMetadatum>;
  onFiltersApply: () => void;
  resetAll: () => void;
  resetDiscoverSort: () => void;
  resetDiscoverFilters: () => void;
  loading: boolean;
  initialLoading: boolean;
  topActiveEnabled: boolean;
  newlyJoinedEnabled: boolean;
  topEarnersEnabled: boolean;
  initialFilters: FiltersSortContextType<DiscoverFilterFields>["filters"];
  freshCreatorStatPublicKeys: string[];
  setFiltersToLastSaved: () => void;
  syncLastSavedFilters: () => void;
  isFilterDisabled: (name: DiscoverFilterFields) => boolean;
  isPinnedCreator: (publicKey: string) => boolean;
  filterBlockedAccount: (publicKey: string) => void;
  blockedAccounts: string[];
  loadingMoreCreators: boolean;
  hasMoreCreators: boolean;
} & FiltersSortContextType<DiscoverFilterFields>;

const DiscoverContext = createContext<DiscoverContextType | undefined>(
  undefined,
);

// Note: filters should work on BOTH queries IDENTICALLY, if not - ask BE team for assistance
export const DiscoverProvider = ({
  children,
  useStorage = true,
  extraFilters = {},
  fetchFreshItems = false,
  initialFilters = [
    {
      name: "totalPosts",
      type: FilterType.numericWithTime,
      value: {
        min: 0,
        timePeriod: TimePeriod.Past24H,
      },
    },
  ],
  initialSort = {
    type: SortType.basic,
    name: DiscoverFilterFields.totalAssetsBalanceUsdCents,
    direction: "desc",
  } as BasicSort<DiscoverFilterFields>,
  disabledFilters = [],
  queryToUse = "creatorStats",
}: {
  children: ReactNode;
  initialFilters?: FiltersSortContextType<DiscoverFilterFields>["filters"];
  initialSort?: SortVariant<DiscoverFilterFields>;
  useStorage?: boolean;
  fetchFreshItems?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraFilters?: Record<string, any>;
  disabledFilters?: DiscoverFilterFields[];
  queryToUse?: "creatorStats" | "tokenMetadata";
}) => {
  const { currentUserPublicKey } = useContext(UserContext);
  const [discoverFiltersAndSort, saveDiscoverFiltersAndSort] = useLocalStorage<{
    filters: FilterRecord[];
    currentSort: SortVariant<DiscoverFilterFields> | null;
  } | null>(LocalStorageKeys.discoverFiltersAndSort, {
    filters: initialFilters,
    currentSort: initialSort,
  });

  const [blockedAccounts, setBlockedAccounts] = useState<string[]>([]);

  const filterBlockedAccount = useCallback((publicKey: string) => {
    setBlockedAccounts((prev) => [...prev, publicKey]);
  }, []);

  const filtersAndSort = useMemo(() => {
    return useStorage
      ? {
          initialFilters: discoverFiltersAndSort?.filters || [],
          initialSort: discoverFiltersAndSort?.currentSort || null,
          disabledFilters,
        }
      : {
          initialFilters,
          initialSort,
          disabledFilters,
        };
  }, [
    disabledFilters,
    discoverFiltersAndSort?.currentSort,
    discoverFiltersAndSort?.filters,
    initialFilters,
    initialSort,
    useStorage,
  ]);

  const {
    setFilters,
    findFilterByName,
    removeFilter,
    filters,
    isSortActive,
    isFilterActive,
    updateFilter,
    currentSort,
    syncLastSavedFilters,
    setFiltersToLastSaved,
    setCurrentSort,
    isFilterDisabled,
  } = useFiltersAndSort(filtersAndSort);

  const [creators, setCreators] = useState<Array<CreatorStat | TokenMetadatum>>(
    [],
  );

  const [totalCount, setTotalCount] = useState(0);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [startCursor, setStartCursor] = useState<string | null>(null);
  const [hasMoreFreshItems, setHasMoreFreshItems] = useState(true);
  const [freshCreatorStatPublicKeys, setFreshCreatorStatPublicKeys] = useState<
    string[]
  >([]);
  const requestVersionRef = useRef(0);

  const { data: pinnedCreators, loading: loadingPinnedCreators } = useQuery(
    TokenMetadataQueryDocument,
    {
      variables: {
        filter: {
          publicKeyBase58Check: {
            in: PINNED_TOKEN_PUBLIC_KEYS,
          },
          ...extraFilters,
        },
      },
      skip: queryToUse === "creatorStats",
    },
  );

  const [fetchCreators, { loading: loadingCreators }] =
    useLazyQuery(CreatorStatsDocument);

  const [fetchTokenMetadataStats, { loading: loadingTokenMetadataStats }] =
    useLazyQuery(TokenMetadataQueryDocument);

  const hasMoreCreators = totalCount > creators.length;

  const syncFiltersWithLocalStorage = useCallback(() => {
    if (!useStorage) return;
    saveDiscoverFiltersAndSort({
      filters,
      currentSort,
    });
  }, [useStorage, saveDiscoverFiltersAndSort, filters, currentSort]);

  const getFilters = useCallback(() => {
    const baseFilters =
      queryToUse === "creatorStats"
        ? {
            publicKeyBase58Check: {
              notIn: PINNED_TOKEN_PUBLIC_KEYS,
            },
            account: {
              isBlacklisted: {
                equalTo: false,
              },
            },
          }
        : {
            publicKeyBase58Check: {
              notIn: PINNED_TOKEN_PUBLIC_KEYS,
            },
          };

    const f = filters.reduce(
      (acc, filter) => {
        if (filter.type === FilterType.numericWithTime) {
          acc[filter.name + filter.value.timePeriod] = {
            greaterThanOrEqualTo: filter.value.min,
            lessThanOrEqualTo: filter.value.max,
          };
        }

        if (filter.type === FilterType.boolean) {
          acc[filter.name] = {
            equalTo: filter.value,
          };
        }

        if (filter.type === FilterType.string) {
          acc[filter.name] = {
            equalToInsensitive: filter.value,
          };
        }

        if (filter.type === FilterType.matchString) {
          const anyArrayOfStrings = filter.value.any?.split(" ") || [];
          const allArrayOfStrings = filter.value.all?.split(" ") || [];
          const noneArrayOfStrings = filter.value.none?.split(" ") || [];

          const anyFilter = {
            or: anyArrayOfStrings.map((word) => {
              return {
                description: {
                  likeInsensitive: `%${word}%`,
                },
              };
            }),
          };

          const allFilter = {
            and: allArrayOfStrings.map((word) => {
              return {
                description: {
                  likeInsensitive: `%${word}%`,
                },
              };
            }),
          };

          const noneFilter = {
            and: noneArrayOfStrings.map((word) => {
              return {
                description: {
                  notLikeInsensitive: `%${word}%`,
                },
              };
            }),
          };

          acc.or = [anyFilter, allFilter, noneFilter];
        }

        if (filter.type === FilterType.numeric) {
          acc[filter.name] = {
            greaterThanOrEqualTo: Number(filter.value.min),
            lessThanOrEqualTo: Number(filter.value.max),
          };
        }

        if (filter.type === FilterType.date) {
          const after = filter.value.after;
          const before = filter.value.before;

          acc[filter.name] = {
            greaterThanOrEqualTo:
              typeof after === "string" ? after : after?.toISOString(),
            lessThanOrEqualTo:
              typeof before === "string" ? before : before?.toISOString(),
          };
        }

        return acc;
      },
      baseFilters as Record<string, unknown>,
    );

    Object.entries(extraFilters).forEach(([key, value]) => {
      f[key] = value;
    });

    return f;
  }, [extraFilters, filters, queryToUse]);

  const getOrderBy = useCallback(() => {
    const orderBy = [];

    if (currentSort) {
      const name = currentSort.name;

      const direction = currentSort.direction || "asc";

      orderBy.push(
        currentSort?.type === SortType.basic
          ? `${SORT_KEYS[name]}_${SORT_VALUE_MAP[direction]}`
          : `${SORT_KEYS[name]}_${TIME_RANGE_SORT_KEYS[currentSort.timePeriod]}_${SORT_VALUE_MAP[direction]}`,
      );
    }

    return orderBy;
  }, [currentSort]);

  const getCreators = async () => {
    const requestVersion = requestVersionRef.current;
    const variables: Record<string, unknown> = {
      first: 10,
      after: endCursor,
      tokenBalancesFilter: {
        creator: {
          publicKey: {
            equalTo: currentUserPublicKey,
          },
        },
      },
    };

    const f = getFilters();

    if (Object.keys(f).length) {
      variables.filter = f;
    }

    const orderBy = getOrderBy();

    if (orderBy.length) {
      variables.orderBy = orderBy as CreatorStatsOrderBy[];
    }

    const { newCreators, newStartCursor, totalCount, newEndCursor } =
      await (async () => {
        if (queryToUse === "creatorStats") {
          const request = fetchCreators({
            variables,
          });
          request.retain?.();
          const { data } = await request;

          return {
            totalCount: data?.creatorStats?.totalCount ?? 0,
            newEndCursor: data?.creatorStats?.pageInfo.endCursor ?? null,
            newStartCursor: data?.creatorStats?.pageInfo.startCursor ?? null,
            newCreators: data?.creatorStats?.nodes.filter(Boolean) ?? [],
          };
        } else {
          const request = fetchTokenMetadataStats({
            variables,
          });
          request.retain?.();
          const { data } = await request;

          return {
            newCreators:
              data?.tokenMetadata?.nodes.filter(Boolean) ??
              ([] as TokenMetadatum[]),
            newStartCursor: data?.tokenMetadata?.pageInfo.startCursor ?? null,
            totalCount: data?.tokenMetadata?.totalCount ?? 0,
            newEndCursor: data?.tokenMetadata?.pageInfo.endCursor ?? null,
          };
        }
      })();

    if (requestVersion !== requestVersionRef.current) {
      return;
    }

    if (!startCursor) {
      setStartCursor(newStartCursor);
    }

    setEndCursor(newEndCursor);
    setTotalCount(totalCount);
    setCreators((creators) => {
      const uniqueCreators = new Map(
        creators.map((creator) => [creator.publicKeyBase58Check, creator]),
      );
      (newCreators as CreatorStat[]).forEach((creator) => {
        uniqueCreators.set(creator.publicKeyBase58Check, creator);
      });
      return Array.from(uniqueCreators.values());
    });
    syncFiltersWithLocalStorage();
  };

  const fetchFreshCreators = useCallback(async () => {
    if (!fetchFreshItems || !hasMoreFreshItems || !startCursor) return;
    const requestVersion = requestVersionRef.current;

    const variables: Record<string, unknown> = {
      first: 1,
      before: startCursor,
      tokenBalancesFilter: {
        creator: {
          publicKey: {
            equalTo: currentUserPublicKey,
          },
        },
      },
    };

    const f = getFilters();

    if (Object.keys(f).length) {
      variables.filter = f;
    }

    const orderBy = getOrderBy();

    if (orderBy.length) {
      variables.orderBy = orderBy as CreatorStatsOrderBy[];
    }

    const { newCreators, newStartCursor } = await (async () => {
      if (queryToUse === "creatorStats") {
        const request = fetchCreators({
          variables,
        });
        request.retain?.();
        const { data } = await request;

        return {
          newCreators:
            data?.creatorStats?.nodes.filter(Boolean) ?? ([] as CreatorStat[]),
          newStartCursor: data?.creatorStats?.pageInfo.startCursor ?? null,
        };
      } else {
        const request = fetchTokenMetadataStats({
          variables,
        });
        request.retain?.();
        const { data } = await request;

        return {
          newCreators:
            data?.tokenMetadata?.nodes.filter(Boolean) ??
            ([] as TokenMetadatum[]),
          newStartCursor: data?.tokenMetadata?.pageInfo.startCursor ?? null,
        };
      }
    })();

    if (requestVersion !== requestVersionRef.current) {
      return;
    }

    setHasMoreFreshItems(newCreators.length === 5 && !!newStartCursor);

    if (newStartCursor) {
      setStartCursor(newStartCursor);
    }

    setFreshCreatorStatPublicKeys((publicKeys) => [
      ...new Set([
        ...publicKeys,
        ...newCreators
          .map((creator) => creator.publicKeyBase58Check ?? "")
          .filter(Boolean),
      ]),
    ]);

    setCreators((creators) => [...(newCreators as CreatorStat[]), ...creators]);
  }, [
    currentUserPublicKey,
    fetchCreators,
    fetchFreshItems,
    fetchTokenMetadataStats,
    getFilters,
    getOrderBy,
    hasMoreFreshItems,
    queryToUse,
    startCursor,
  ]);

  useEffect(() => {
    if (fetchFreshItems) {
      const interval = setInterval(fetchFreshCreators, 1000);

      return () => clearInterval(interval);
    }
  }, [fetchFreshCreators, fetchFreshItems, hasMoreFreshItems, startCursor]);

  const { loadingMarkup, setInitialLoading, initialLoading, ref, loadingMore } =
    usePaginationRequest({
      initialRequest: getCreators,
      fetchRequest: getCreators,
      hasMoreItems: hasMoreCreators,
    });

  const onFiltersApply = useCallback(async () => {
    requestVersionRef.current += 1;
    setCreators([]);
    setEndCursor(null);
    setStartCursor(null);
    setFreshCreatorStatPublicKeys([]);
    setHasMoreFreshItems(true);
    setInitialLoading(true);
  }, [setInitialLoading]);

  const resetAll = useCallback(() => {
    requestVersionRef.current += 1;
    setFilters([], true);
    setCurrentSort(null, true);
    setCreators([]);
    setEndCursor(null);
    setStartCursor(null);
    setFreshCreatorStatPublicKeys([]);
    setHasMoreFreshItems(true);
  }, [setCurrentSort, setFilters]);

  const resetDiscoverFilters = useCallback(() => {
    setFilters(filtersAndSort.initialFilters, true);
    void onFiltersApply();
  }, [filtersAndSort.initialFilters, onFiltersApply, setFilters]);

  const resetDiscoverSort = useCallback(() => {
    setCurrentSort(initialSort, true);
    void onFiltersApply();
  }, [initialSort, onFiltersApply, setCurrentSort]);

  const { topActiveEnabled, newlyJoinedEnabled, topEarnersEnabled } = (() => {
    const topActiveEnabled =
      filters.length === 1 &&
      isEqual(filters[0], topActiveFilterConfig) &&
      isEqual(currentSort, topActiveSortConfig);
    const newlyJoinedEnabled =
      filters.length === 1 &&
      isEqual(filters[0], newlyJoinedFilterConfig) &&
      isEqual(currentSort, newlyJoinedSortConfig);
    const topEarnersEnabled =
      isEqual(currentSort, topEarnersSortConfig) && !filters.length;

    return {
      topActiveEnabled,
      newlyJoinedEnabled,
      topEarnersEnabled,
    };
  })();

  const pinnedCreatorsList = (pinnedCreators?.tokenMetadata?.nodes.filter(
    Boolean,
  ) ?? []) as Array<CreatorStat | TokenMetadatum>;

  const isPinnedCreator = useCallback(
    (publicKey: string) =>
      pinnedCreatorsList.some(
        (creator) => creator.publicKeyBase58Check === publicKey,
      ),
    [pinnedCreatorsList],
  );

  const finalCreators = useMemo(() => {
    return creators.filter(
      (creator) =>
        !blockedAccounts.includes(creator.publicKeyBase58Check ?? ""),
    );
  }, [creators, blockedAccounts]);

  return (
    <DiscoverContext.Provider
      value={{
        syncLastSavedFilters,
        setFiltersToLastSaved,
        resetAll,
        onFiltersApply,
        lazyLoadingMarkup: loadingMarkup,
        lazyLoadingRef: ref,
        creators: finalCreators,
        pinnedCreators: pinnedCreatorsList,
        setFilters,
        findFilterByName,
        removeFilter,
        filters,
        isSortActive,
        isFilterActive,
        updateFilter,
        currentSort,
        resetDiscoverSort,
        resetDiscoverFilters,
        setCurrentSort,
        loading: loadingCreators || loadingTokenMetadataStats,
        initialLoading: initialLoading || loadingPinnedCreators,
        topActiveEnabled,
        newlyJoinedEnabled,
        topEarnersEnabled,
        initialFilters,
        freshCreatorStatPublicKeys,
        isFilterDisabled,
        isPinnedCreator,
        filterBlockedAccount,
        blockedAccounts,
        loadingMoreCreators: loadingMore,
        hasMoreCreators,
      }}
    >
      {children}
    </DiscoverContext.Provider>
  );
};

export const useDiscoverContext = () => {
  const context = useContext(DiscoverContext);
  if (!context) {
    throw new Error(
      "useDiscoverContext must be used within a DiscoverProvider",
    );
  }
  return context;
};

export const topActiveSortConfig = {
  type: SortType.basic,
  name: DiscoverFilterFields.totalAssetsBalanceUsdCents,
  direction: "desc",
};

export const newlyJoinedSortConfig = {
  type: SortType.basic,
  name: DiscoverFilterFields.totalAssetsBalanceUsdCents,
  direction: "desc",
};

export const topEarnersSortConfig = {
  type: SortType.numericWithTime,
  name: DiscoverFilterFields.totalFocusEarningsUsdCents,
  direction: "desc",
  timePeriod: TimePeriod.Past30D,
};

export const topActiveFilterConfig = {
  name: DiscoverFilterFields.totalPosts,
  type: FilterType.numericWithTime,
  value: {
    min: 1,
    timePeriod: TimePeriod.Past30D,
  },
};

export const newlyJoinedFilterConfig = {
  name: DiscoverFilterFields.joinedFocusDate,
  type: FilterType.date,
  value: {
    after: FocusDateParser.subtract(30, "day"),
  },
};
